import { OptionallyVisible } from 'components';
import { useTranslation } from 'react-i18next';
import assetsApi from 'services/assets';
import { PayoutDestinationType } from 'services/payouts/types';

import { Button } from 'components/buttons';
import IconCross from 'components/icons/cross';
import { BarSwitch, BarSwitchOption } from 'components/inputs/switch/barSwitch';
import LoadingIndicator from 'components/loadingIndicator/loadingIndicator';
import { ModalControls } from 'components/modal/styles';

import { PayoutDestinationsDataRow } from '../../types';
import { usePayout } from './context';
import { SetupPayoutStep, usePayoutSetupStep } from './context/modal';
import CryptoPayout from './crypto';
import WalletDetails from './crypto/wallet';
import FiatPayout from './fiat';
import BankAccountSelection from './fiat/bankAccountSelection';
import PayoutBankDetails from './fiat/details';
import Memo from './fiat/memo';
import PayoutSchedule from './fiat/schedule';
import LABELS, { KEYS } from './keys';
import { ErrorText, Label, ModalContent, ModalContentWrapper, ModalHead, ModalLabel, Section, StyledDialog } from './styles';
import { PayoutType } from './types';

interface PayoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  payoutDestinations: PayoutDestinationsDataRow[];
}

const PayoutModal = ({ isOpen, onClose, payoutDestinations }: PayoutModalProps) => {
  const { t } = useTranslation();
  const payoutStep = usePayoutSetupStep();
  const payout = usePayout();
  const { data: assetsWithPayoutReponse, isLoading, error } = assetsApi.useGetPWCAssetListQuery(payout.merchant.uuid);

  const payoutOptions: BarSwitchOption[] = [
    {
      value: PayoutType.Fiat,
      label: t(LABELS.PAYOUT_TYPE[PayoutType.Fiat]),
    },
    {
      value: PayoutType.Crypto,
      label: t(LABELS.PAYOUT_TYPE[PayoutType.Crypto]),
    },
  ];

  const step = payoutStep[payoutStep.type];
  const showPayoutTypeSwitch = step === SetupPayoutStep.SelectPayoutType;
  const assetFeesFiat = assetsWithPayoutReponse?.payoutDestinations?.filter(
    (destination) => destination.type === PayoutDestinationType.BANK_ACCOUNT,
  );
  const assetFeesCrypto = assetsWithPayoutReponse?.payoutDestinations?.filter(
    (destination) => destination.type === PayoutDestinationType.CRYPTO,
  );

  const modalContent = {
    [PayoutType.Fiat]: {
      [SetupPayoutStep.SelectPayoutType]: <FiatPayout payoutDestinations={assetFeesFiat} />,
      [SetupPayoutStep.SelectBankAccount]: <BankAccountSelection payoutDestinations={payoutDestinations} />,
      [SetupPayoutStep.AccountDetails]: <PayoutBankDetails />,
      [SetupPayoutStep.Memo]: (
        <Memo
          skippable
          merchant={payout.merchant}
          paymentRail={payout[PayoutType.Fiat].paymentRail!}
          proceed={payoutStep.proceed}
          onClose={onClose}
        />
      ),
      [SetupPayoutStep.Schedule]: (
        <PayoutSchedule
          paymentRail={payout[PayoutType.Fiat].paymentRail}
          merchant={payout.merchant}
          payoutType={payoutStep.type}
          onSuccess={payoutStep.close}
          onClose={payoutStep.close}
        />
      ),
    },
    [PayoutType.Crypto]: {
      [SetupPayoutStep.SelectPayoutType]: <CryptoPayout assets={assetsWithPayoutReponse?.assets} payoutDestinations={assetFeesCrypto} />,
      [SetupPayoutStep.WalletDetails]: <WalletDetails />,
      [SetupPayoutStep.Schedule]: (
        <PayoutSchedule merchant={payout.merchant} payoutType={payoutStep.type} onSuccess={payoutStep.close} onClose={payoutStep.close} />
      ),
    },
  }[payoutStep.type][step];

  const onPayoutTypeSelect = ({ value }: BarSwitchOption) => {
    payoutStep.setPayoutSetupStepState({
      ...payoutStep,
      type: value as PayoutType,
    });
  };

  if (isLoading) {
    return (
      <StyledDialog open={isOpen} scroll="body" disableEscapeKeyDown fullWidth>
        <ModalContentWrapper>
          <LoadingIndicator padded />
        </ModalContentWrapper>
      </StyledDialog>
    );
  }

  if (error) {
    return (
      <StyledDialog open={isOpen} scroll="body" disableEscapeKeyDown fullWidth>
        <ModalContentWrapper>
          <ErrorText>{(error as any)?.data?.errorMessage || t(LABELS.LOADING_ASSETS_ERORR)}</ErrorText>
        </ModalContentWrapper>
      </StyledDialog>
    );
  }

  return (
    <StyledDialog open={isOpen} scroll="body" disableEscapeKeyDown fullWidth>
      <ModalContentWrapper>
        <ModalControls>
          <ModalHead>
            <ModalLabel>{t(LABELS.MODAL_TITLE)}</ModalLabel>
            <div id={KEYS.MODAL_HEADER_ID} />
            <Button flat onClick={onClose}>
              <IconCross />
            </Button>
          </ModalHead>
        </ModalControls>

        <ModalContent>
          <OptionallyVisible visible={showPayoutTypeSwitch}>
            <Section>
              <Label>{t(LABELS.PAYOUT_TYPE_LABEL)}</Label>
              <BarSwitch value={payoutStep.type} onChange={onPayoutTypeSelect} options={payoutOptions} />
            </Section>
          </OptionallyVisible>
          {modalContent}
        </ModalContent>
      </ModalContentWrapper>
    </StyledDialog>
  );
};

export default PayoutModal;
